import { z } from 'zod';

import { SavedColumnLayoutSchema } from '@/domain/documents/savedColumnLayout';

// The Documents workspace grid layout: what a preset stores, what the slice holds, what the browser keeps.
// Always left-to-right: RTL swaps pinning when it reaches or leaves the grid (`@/domain/documents/gridPreset/convert`).

export const GridLayoutSortSchema = z.object({
  colId: z.string(),
  sort: z.enum(['asc', 'desc']),
});
export type GridLayoutSort = z.infer<typeof GridLayoutSortSchema>;

export const GridLayoutSchema = z.object({
  filters: z.record(z.string(), z.unknown()),
  sorting: z.array(GridLayoutSortSchema),
  // Data columns only, in display order, each with what the workspace applies (`@/domain/documents/savedColumnLayout`);
  // `[]` means "the column definitions as they come".
  columns: SavedColumnLayoutSchema,
});
export type GridLayout = z.infer<typeof GridLayoutSchema>;

// The parts of the working layout that differ from the current preset's saved version.
export const GridLayoutChangesSchema = GridLayoutSchema.partial();
export type GridLayoutChanges = z.infer<typeof GridLayoutChangesSchema>;

// What the browser keeps per user and company (`@/domain/documents/gridPreset/persistence`). Only the unsaved parts
// are kept, not the whole working layout: a preset saved elsewhere (another company for an "(all)" preset, another
// tab) then opens as newly saved, with only the parts changed here laid over it.
export const StoredGridPresetSchema = z.object({
  presetId: z.string(),
  // Set when the current preset came from a shared link: it is not in the user's list, so a reload fetches it by id.
  sharedPresetId: z.string().nullish(),
  // Absent when nothing differs from the saved version.
  changes: GridLayoutChangesSchema.optional(),
});
export type StoredGridPreset = z.infer<typeof StoredGridPresetSchema>;
